import Vue from 'vue';
import Vuex from 'vuex';
import * as firebase from "firebase";
import { deviceType } from "mobile-device-detect";

Vue.use(Vuex);

// MatterportのSDK Key
// const matterportSDKKey: string = 'a4ac4ec6fb7e49ba9e7e4fd823bb4355';
// For-TOW
const matterportSDKKey: string = '4411c3e9db384a3c91586c57c0541d32';

// 本番公開時のPublicPath
// const prodPublicPath: string = '/demo/vr-mockup';
// For-TOW
const prodPublicPath: string = '/online_event_package/demo/vree-mockup';

export default new Vuex.Store({
  state: {
    // SDK Key
    matterport_sdk_key: matterportSDKKey,
    // サイトの公開パス
    publicPath: process.env.NODE_ENV === 'production' ? prodPublicPath : '',
    // スマートフォンデバイス
    isSmp: deviceType === 'mobile',
    // ログインユーザー
    user: null,
    // サイトアクセス時のユーザー存在チェックを通っているかどうか
    isFirstUserCheck: false,
    // サイトにログインしているかどうか
    isLoggedIn: false,
    // ライブステージを開くためのトリガー変数
    isOpenLiveStreaming: false,
    // ライブステージのステージ番号
    liveContentNo: null,
    // BGMが再生中かどうか
    isBgmPlaying: false,
    // BGMがミュートになっているかどうか
    isBgmMuted: true
  },
  
  getters: {
    /**
     * ユーザーがログインしているかどうかを取得
     * @param state
     */
    logged_in (state): boolean {
      return state.isLoggedIn;
    },
    
    /**
     * ゲストユーザーでログインしている場合に使用するゲストユーザーのアイコン画像URLを取得
     * @param state
     */
    guestUserIcon (state): string {
      return state.publicPath + '/images/guest-thumbnail-icon.png';
    },
    
    /**
     * 各ユーザーのアイコン写真が空だったり404になった場合に使用する代替画像のURLを取得
     * @param state
     */
    noImageUserIcon (state): string {
      return state.publicPath + '/images/no-image-thumbnail-icon.png';
    }
  },
  
  mutations: {
    /**
     * 最初のユーザーの存在チェックを実行し、フラグをONにする
     * @param state
     */
    doFirstUserCheck (state) {
      state.isFirstUserCheck = true;
    },
    
    /**
     * ログインされたユーザーのユーザー情報をcommitする
     * @param state
     * @param uid Firebase上に登録されたユーザーID
     * @param name ユーザー名
     * @param thumbnailUrl ユーザーサムネイル画像のURL
     * @param isGuest ゲストユーザーかどうか
     */
    setLoggedInUser(state, { uid, name, thumbnailUrl, isGuest }) {
      // stateにユーザー情報を設定
      state.user = {
        uid: uid,
        name: name,
        thumbnailUrl: thumbnailUrl,
        isGuest: !!isGuest
      };
      // ログインしているかどうかのフラグをONにする
      state.isLoggedIn = true;
    },
    
    /**
     * ログアウト完了時に設定されていたユーザー情報をクリアにする
     * @param state
     */
    clearLoggedInUser(state) {
      // ユーザー情報を空にする
      state.user = null;
      // ログインフラグをOFFにする
      state.isLoggedIn = false;
      // 初回のユーザー存在チェックをOFFにする
      state.isFirstUserCheck = false;
    },
  
    /**
     * ライブストリーミングコンテンツをスタートする
     * @param state
     * @param liveContentNo ステージ番号
     */
    openLiveStreaming (state, { liveContentNo }) {
      state.liveContentNo = liveContentNo;
      state.isOpenLiveStreaming = true;
    },
  
    /**
     * ライブストリーミングコンテンツをクローズする
     * @param state
     */
    // ToDo メソッド名を変更する
    closeLiveStreaming (state) {
      state.liveContentNo = null;
      state.isOpenLiveStreaming = false;
    },
  
    /**
     * BGMを再生する
     * @param state
     */
    playBgm (state) {
      if (!state.isBgmMuted) {
        state.isBgmPlaying = true;
      }
    },
  
    /**
     * BGMを停止する
     * @param state
     */
    pauseBgm (state) {
      if (!state.isBgmMuted) {
        state.isBgmPlaying = false;
      }
    },
  
    /**
     * BGMのON／OFFを切り替える
     * @param state
     * @param toggle ONかOFFか
     */
    toggleBgm (state, { toggle }) {
      if (toggle) {
        state.isBgmMuted = false;
        state.isBgmPlaying = true;
      }
      else {
        state.isBgmMuted = true;
        state.isBgmPlaying = false;
      }
    },
  
    /**
     * BGMをリセットする
     * @param state
     */
    resetBgm (state) {
      // ミュートにする
      state.isBgmMuted = true;
      // 再生フラグをOFFにする
      state.isBgmPlaying = false;
    }
  },
  
  actions: {
    /**
     * ライブストリーミングコンテンツをスタートさせる
     * @param commit
     * @param state
     * @param contentNo {number} コンテンツの番号
     */
    startLiveStreaming ({ commit }, { contentNo } ): void {
      console.log('Start Live Streaming: ' + contentNo);
      commit('openLiveStreaming', { liveContentNo: contentNo });
    },
  
    /**
     * ライブストリーミングコンテンツを終了する
     * @param commit
     */
    endLiveStreaming ({ commit }): void {
      commit('closeLiveStreaming');
    },
  
    /**
     * ログインアクション
     * @param state
     * @param commit
     */
    login ({ state, commit }): Promise<any> {
      return new Promise((resolve, reject) => {
        // Authオブジェクトの状態変化の監視リスナーをセット
        firebase.auth().onAuthStateChanged((user) => {
          if (user) {
            console.log('Success Login: ', user);
            console.log('Provider Data: ', user.providerData);
            if (user.isAnonymous) {
              // ゲストユーザーの設定
              const guestDisplayName: string = 'ゲスト' + String(Math.ceil(Math.random() * 99999));
              // ログイン情報をセットする
              commit('setLoggedInUser', {
                uid: user.uid,
                name: guestDisplayName,
                thumbnailUrl: '/images/guest-thumbnail-icon.png',
                isGuest: true
              });
            }
            else {
              // SNSのoAuthログイン
              console.log('ProviderID: ', user.providerData[0].providerId);
              // Facebook: facebook.com
              // Twitter: twitter.com
              // Google: google.com
              // ログイン情報をセットする
              commit('setLoggedInUser', {
                uid: user.uid,
                name: user.displayName,
                thumbnailUrl: user.photoURL,
                isGuest: false
              });
            }
            
            // まだ初回のログインチェックを実施していなかった場合
            if (!state.isFirstUserCheck) {
              commit('doFirstUserCheck');
            }
            
            resolve(0);
          }
          else {
            // ユーザー情報が返ってこなかった場合はエラーをrejectする
            const err = new Error('login_failed');
            // まだ初回のログインチェックを実施していなかった場合
            if (!state.isFirstUserCheck) {
              commit('doFirstUserCheck');
            }
            reject(err);
          }
        });
      });
    },
  
    /**
     * ログアウトする
     * @param commit
     */
    logout ({ commit }): Promise<any> {
      return new Promise((resolve, reject) => {
        // Authオブジェクトにサインアウトをコール
        firebase.auth().signOut()
          .then((result) => {
            // ログイン情報をクリアにする
            commit('clearLoggedInUser');
            resolve(0);
          })
          .catch((err) => {
            reject(err);
          })
      });
    }
  },
  modules: {
  }
})
